import {Button, IconButton} from '@wandb/components';
import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {produce} from 'immer';
import _ from 'lodash';
import React, {type JSX, type ReactNode} from 'react';
import {Popup} from 'semantic-ui-react';

import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {DEFAULT_RUNS_SORT, DEFAULT_RUNS_SORT_KEY} from '../../../util/queryts';
import type * as QueryTypes from '../../../util/queryTypes';
import * as Run from '../../../util/runs';
import * as UIHelpers from '../../../util/uihelpers';

interface WBTableActionSortButtonProps {
  onPress: () => void;
  compact?: boolean;
  disabled?: boolean;
  sortOpen: boolean;
  defaultSort?: QueryTypes.Sort;
  isInReport?: boolean;
  sort: QueryTypes.Sort;
}

export const WBTableActionSortButton = React.memo(
  (props: WBTableActionSortButtonProps) => {
    const isActive =
      props.sortOpen ||
      !_.isEqual(
        props.sort.keys,
        props.defaultSort?.keys ?? DEFAULT_RUNS_SORT.keys
      );

    const handleAnalytics = () => {
      const actionLocation = props.isInReport ? 'report' : 'runs table';
      window.analytics?.track('Sort dropdown clicked', {
        location: actionLocation,
      });
    };

    if (props.compact) {
      return (
        <IconButton
          icon="sort"
          isActive={isActive}
          variant="secondary"
          isDisabled={props.disabled}
          data-test="sort-button"
          onPress={() => {
            handleAnalytics();
            props.onPress();
          }}
          tooltip={{children: 'Sort'}}
          aria-label="Sort"
        />
      );
    }
    return (
      <Button
        startIcon="sort"
        isActive={isActive}
        variant="secondary"
        isDisabled={props.disabled}
        onPress={() => {
          handleAnalytics();
          props.onPress();
        }}
        data-test="sort-button">
        Sort
      </Button>
    );
  }
);

interface WBTableActionSortDropdownProps {
  sortableKeys: string[];
  loading: boolean;
  sort: QueryTypes.Sort;
  sortKeyIdx: number;

  setSort(sort: QueryTypes.Sort): void;
}

export const WBTableActionSortDropdown = (
  props: WBTableActionSortDropdownProps
) => {
  const {sortableKeys, loading, sort, sortKeyIdx, setSort} = props;
  const sortKey = sort.keys[sortKeyIdx] ?? DEFAULT_RUNS_SORT_KEY;
  const options = UIHelpers.makeOptions(sortableKeys);
  return (
    <ModifiedDropdown
      data-test="sort-dropdown"
      className="sort"
      placeholder="Sort by..."
      search={UIHelpers.searchFunction}
      selection
      lazyLoad
      optionTransform={UIHelpers.beautify}
      itemLimit={100}
      options={options}
      value={Run.keyToString(sortKey.key)}
      disabled={loading}
      loading={loading}
      onChange={(e, {value}) => {
        // We know it's string because we pass in options
        const typedValue: string = value as string;
        const key = Run.keyFromString(typedValue);
        if (key != null) {
          setSort(
            produce(sort, draft => {
              draft.keys[sortKeyIdx].key = key;
            })
          );
        }
      }}
    />
  );
};

interface WBTableActionSortPickerProps {
  defaultSort?: QueryTypes.Sort; // the default sort
  sort: QueryTypes.Sort; // the current sort

  makeDropdown(sortKeyIdx: number): JSX.Element;
  setSort(sort: QueryTypes.Sort): void;
}

export const WBTableActionSortPicker = (
  props: WBTableActionSortPickerProps
) => {
  const {defaultSort, sort, makeDropdown, setSort} = props;
  return (
    <Tailwind>
      <div className="mb-8 font-semibold text-moon-800 dark:text-moon-200">
        Sort runs by...
      </div>
      <div className="flex flex-col gap-8">
        {sort.keys.map(({key, ascending}, i) => (
          <div
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={i}
            className={'flex items-center gap-8'}
            data-test="sort-item">
            {sort.keys.length > 1 && (
              <IconButton
                icon="close"
                aria-label="Remove sort key"
                variant="ghost"
                onClick={() => {
                  if (sort.keys.length <= 1) {
                    return;
                  }
                  setSort(
                    produce(sort, draft => {
                      draft.keys.splice(i, 1);
                    })
                  );
                }}
              />
            )}
            {makeDropdown(i)}
            <IconButton
              variant="secondary"
              data-test="sort-order"
              aria-label={`Toggle sort order (currently ${
                ascending ? 'ascending' : 'descending'
              })`}
              icon={ascending ? 'up' : 'down'}
              onClick={() => {
                setSort(
                  produce(sort, draft => {
                    draft.keys[i].ascending = !draft.keys[i].ascending;
                  })
                );
              }}
            />
          </div>
        ))}
      </div>
      <hr className="mt-16 border-wb-border" />
      <div className="mt-16 flex flex-col gap-8">
        <div
          data-test="sort-add"
          data-perf-action="run-sort-add"
          className="cursor-pointer font-semibold	text-wb-text-link"
          onClick={() => {
            setSort(
              produce(sort, draft => {
                draft.keys.push(
                  defaultSort?.keys?.[0] ?? DEFAULT_RUNS_SORT_KEY
                );
              })
            );
          }}>
          Add another field
        </div>
        <div
          data-test="sort-reset"
          data-perf-action="run-sort-reset"
          className="cursor-pointer font-semibold text-wb-text-link"
          onClick={() => setSort(defaultSort ?? DEFAULT_RUNS_SORT)}>
          Reset to default
        </div>
      </div>
    </Tailwind>
  );
};

interface WBTableActionSortProps {
  trigger: (isOpen: boolean) => ReactNode;
  content: ReactNode;
  open: boolean;
  onOpen(): void;
  onClose(): void;
}

export const WBTableActionSort = (props: WBTableActionSortProps) => {
  return (
    <Popup
      basic
      data-test="sort-popup"
      className="wb-table-action-popup"
      // Portal-rendered, so it doesn't inherit the run table's allow; sort
      // keys shown here are already exposed in the table itself.
      data-dd-privacy={DD_PRIVACY_ALLOW}
      on="click"
      position="bottom left"
      open={props.open}
      onOpen={props.onOpen}
      onClose={props.onClose}
      trigger={props.trigger(props.open)}
      content={props.content}
      popperModifiers={{
        preventOverflow: {enabled: false},
        flip: {enabled: false},
      }}
    />
  );
};
